'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  alt: string;
  href: string;
  badge?: string;
  badgeStyle?: 'saffron' | 'green' | 'neutral';
  nutrients: {label: string;value: string;}[];
  accent: string;
  textAccent: string;
}

const products: Product[] = [
{
  id: 'chocolate-chhatua',
  name: 'Chocolate Chhatua',
  tagline: 'Kids love it. Parents approve it.',
  description: 'Wheat, chana & nut breakfast blend with chocolate powder. No artificial colours, no preservatives.',
  image: "/assets/images/product_images/chocolate-chhatua.jpg",
  alt: 'Kasturi Chocolate Chhatua pack — multi-grain breakfast with chocolate powder',
  href: '/products/chocolate-chhatua',
  badge: 'Kids Favourite',
  badgeStyle: 'saffron',
  nutrients: [
  { label: 'Energy', value: '310 kcal' },
  { label: 'Protein', value: '11g' },
  { label: 'Carbs', value: '75g' }],

  accent: '#3D1F0A',
  textAccent: '#E8A838'
},
{
  id: 'mix-chhatua',
  name: 'Mix Chhatua',
  tagline: 'Wholesome. Balanced. Daily.',
  description: 'Wheat, chana, yellow pea & nuts for complete family nutrition. Traditional recipe, modern convenience.',
  image: "/assets/images/product_images/mix-chhatua.jpg",
  alt: 'Kasturi Mix Chhatua multi-grain product pack',
  href: '/products/mix-chhatua',
  nutrients: [
  { label: 'Energy', value: '310 kcal' },
  { label: 'Protein', value: '11g' },
  { label: 'Fibre', value: '1.8g' }],

  accent: '#1A0F05',
  textAccent: '#C17B2F'
},
{
  id: 'sugar-free-chhatua',
  name: 'Sugar-Free Chhatua',
  tagline: 'Zero added sugar. Full nutrition.',
  description: 'Crafted for diabetics and health-conscious households. 13g protein, zero added sugar.',
  image: "/assets/images/product_images/sugar-free-chhatua.jpg",
  alt: 'Kasturi Sugar-Free Chhatua pack — zero added sugar, high protein',
  href: '/products/sugar-free-chhatua',
  badge: 'Sugar Free',
  badgeStyle: 'green',
  nutrients: [
  { label: 'Energy', value: '293 kcal' },
  { label: 'Protein', value: '13g' },
  { label: 'Added Sugar', value: '0g' }],

  accent: '#0D1A0A',
  textAccent: '#7BC67E'
},
{
  id: 'chana-sattu',
  name: 'Chana Sattu',
  tagline: 'Ancient protein. Modern kitchen.',
  description: 'Cold-milled from premium roasted Bengal gram. 20g plant protein per 100g.',
  image: "/assets/images/product_images/chana-sattu.jpg",
  alt: 'Kasturi Chana Sattu pack — high protein roasted gram flour',
  href: '/products/chana-sattu',
  badge: 'High Protein',
  badgeStyle: 'saffron',
  nutrients: [
  { label: 'Protein', value: '20g' },
  { label: 'Fibre', value: '12g' },
  { label: 'Added Sugar', value: '0g' }],

  accent: '#2A1500',
  textAccent: '#E8A838'
},
{
  id: 'chakki-atta',
  name: 'Select Chakki Atta',
  tagline: 'Fortified. Fresh. Fundamental.',
  description: 'Stone-ground whole wheat fortified with Iron, Folic Acid & Vitamin B12. Zero cholesterol.',
  image: "/assets/images/product_images/chakki-atta.jpg",
  alt: 'Kasturi Select Chakki Atta bag — freshly milled wheat flour fortified with micronutrients',
  href: '/products/chakki-atta',
  badge: 'Fortified',
  badgeStyle: 'neutral',
  nutrients: [
  { label: 'Iron', value: '14mg' },
  { label: 'Fibre', value: '13.6g' },
  { label: 'Cholesterol', value: '0mg' }],

  accent: '#1C1005',
  textAccent: '#D4A96A'
},
{
  id: 'ragi-flour',
  name: 'Ragi Flour',
  tagline: 'Calcium-rich. Bone-strong.',
  description: 'Milled from premium finger millet. Naturally rich in calcium and dietary fibre.',
  image: "/assets/images/product_images/ragi-flour.jpg",
  alt: 'Kasturi Ragi Flour 500g pack — finger millet flour with calcium and iron',
  href: '/products/ragi-flour',
  badge: 'New',
  badgeStyle: 'saffron',
  nutrients: [
  { label: 'Energy', value: '382 kcal' },
  { label: 'Protein', value: '11g' },
  { label: 'Fibre', value: '3.5g' }],

  accent: '#0F0A02',
  textAccent: '#C17B2F'
}];


const badgeClasses: Record<string, string> = {
  saffron: 'bg-[#FDF3C7] text-[#8A6A10] border border-[#F5E4A0]',
  green: 'bg-[#FDF3C7] text-[#8A6A10] border border-[#F5E4A0]',
  neutral: 'bg-stone-100 text-stone-600 border border-stone-200'
};

/* ─── HERO CARD (large, spans 2 cols × 2 rows) ─── */
function HeroCard({ product }: {product: Product;}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      href={product.href}
      className="group relative flex flex-col justify-between overflow-hidden rounded-3xl col-span-2 row-span-2 min-h-[420px]"
      style={{ background: product.accent }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          background: `radial-gradient(ellipse 70% 60% at 80% 60%, ${product.textAccent}28 0%, transparent 70%)`,
          opacity: hovered ? 1 : 0.6
        }} />
      
      {/* Noise grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px'
        }} />
      

      {/* Top row: badge + arrow */}
      <div className="relative z-10 flex items-start justify-between p-7">
        {product.badge &&
        <span
          className="text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full"
          style={{ background: `${product.textAccent}22`, color: product.textAccent, border: `1px solid ${product.textAccent}44` }}>
          
            {product.badge}
          </span>
        }
        <span
          className="ml-auto flex items-center justify-center w-9 h-9 rounded-full transition-all duration-300"
          style={{
            background: `${product.textAccent}18`,
            border: `1px solid ${product.textAccent}30`,
            transform: hovered ? 'translate(3px, -3px)' : 'translate(0,0)'
          }}>
          
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={product.textAccent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7M17 7H7M17 7v10" />
          </svg>
        </span>
      </div>

      {/* Product image — centred */}
      <div className="relative z-10 flex justify-center items-center flex-1 px-8">
        <div
          className="transition-transform duration-500"
          style={{
            width: '220px',
            height: '220px',
            transform: hovered ? 'scale(1.06) translateY(-6px)' : 'scale(1)',
            filter: 'drop-shadow(0 24px 48px rgba(0,0,0,0.45))'
          }}>
          
          <AppImage src={product.image} alt={product.alt} width={220} height={220} className="object-contain w-full h-full" />
        </div>
      </div>

      {/* Bottom: text + nutrients */}
      <div className="relative z-10 p-7 pt-4">
        <p className="text-[10px] uppercase tracking-[0.25em] mb-1.5" style={{ color: `${product.textAccent}80` }}>
          Kasturi
        </p>
        <h3
          className="font-display font-[200] leading-tight mb-1"
          style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: '#FDF8F2', letterSpacing: '0.02em' }}>
          
          {product.name}
        </h3>
        <p className="text-sm mb-5" style={{ color: `${product.textAccent}CC` }}>{product.tagline}</p>
        <div className="flex flex-wrap gap-2">
          {product.nutrients.map((n) =>
          <div
            key={n.label}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}>
            
              <span className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.45)' }}>{n.label}</span>
              <span className="text-[10px] font-bold" style={{ color: product.textAccent }}>{n.value}</span>
            </div>
          )}
        </div>
      </div>
    </Link>);

}

/* ─── TALL CARD (spans 1 col × 2 rows) ─── */
function TallCard({ product }: {product: Product;}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      href={product.href}
      className="group relative flex flex-col justify-between overflow-hidden rounded-3xl row-span-2 min-h-[420px]"
      style={{ background: '#FAF7F2', border: '1px solid #EDE8E0' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      {/* Hover accent line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] transition-transform duration-300 origin-left"
        style={{ background: product.textAccent, transform: hovered ? 'scaleX(1)' : 'scaleX(0)' }} />
      

      {/* Badge + arrow */}
      <div className="relative z-10 flex items-start justify-between p-6">
        {product.badge ?
        <span className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${badgeClasses[product.badgeStyle || 'neutral']}`}>
            {product.badge}
          </span> :
        <span />}
        <span
          className="flex items-center justify-center w-8 h-8 rounded-full bg-stone-100 border border-stone-200 transition-all duration-300"
          style={{ transform: hovered ? 'translate(3px, -3px)' : 'translate(0,0)' }}>
          
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#78716c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7M17 7H7M17 7v10" />
          </svg>
        </span>
      </div>

      {/* Image */}
      <div className="relative z-10 flex justify-center items-center flex-1 px-6">
        <div
          className="transition-transform duration-500"
          style={{
            width: '160px',
            height: '160px',
            transform: hovered ? 'scale(1.07) translateY(-5px)' : 'scale(1)',
            filter: 'drop-shadow(0 16px 32px rgba(0,0,0,0.15))'
          }}>
          
          <AppImage src={product.image} alt={product.alt} width={160} height={160} className="object-contain w-full h-full" />
        </div>
      </div>

      {/* Text */}
      <div className="relative z-10 p-6 pt-3">
        <h3 className="font-display font-[200] text-stone-900 leading-tight mb-1" style={{ fontSize: '1.2rem', letterSpacing: '0.02em' }}>
          {product.name}
        </h3>
        <p className="text-xs text-stone-500 mb-4 leading-relaxed">{product.tagline}</p>
        <div className="flex flex-wrap gap-1.5">
          {product.nutrients.map((n) =>
          <div key={n.label} className="flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-stone-100">
              <span className="text-[8px] uppercase tracking-wider text-stone-400">{n.label}</span>
              <span className="text-[9px] font-bold text-stone-700">{n.value}</span>
            </div>
          )}
        </div>
      </div>
    </Link>);

}

/* ─── WIDE CARD (spans 2 cols × 1 row) ─── */
function WideCard({ product }: {product: Product;}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      href={product.href}
      className="group relative flex items-center gap-6 overflow-hidden rounded-3xl col-span-2 min-h-[180px] px-8 py-6"
      style={{ background: product.accent }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          background: `radial-gradient(ellipse 50% 80% at 15% 50%, ${product.textAccent}20 0%, transparent 70%)`,
          opacity: hovered ? 1 : 0.5
        }} />
      
      {/* Image */}
      <div
        className="relative z-10 shrink-0 transition-transform duration-500"
        style={{
          width: '120px',
          height: '120px',
          transform: hovered ? 'scale(1.06) translateY(-4px)' : 'scale(1)',
          filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.4))'
        }}>
        
        <AppImage src={product.image} alt={product.alt} width={120} height={120} className="object-contain w-full h-full" />
      </div>

      {/* Text */}
      <div className="relative z-10 flex-1 min-w-0">
        {product.badge &&
        <span
          className="inline-block text-[9px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full mb-2"
          style={{ background: `${product.textAccent}22`, color: product.textAccent, border: `1px solid ${product.textAccent}44` }}>
          
            {product.badge}
          </span>
        }
        <h3
          className="font-display font-[200] leading-tight mb-1"
          style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)', color: '#FDF8F2', letterSpacing: '0.02em' }}>
          
          {product.name}
        </h3>
        <p className="text-xs mb-3 line-clamp-2" style={{ color: `${product.textAccent}BB` }}>{product.description}</p>
        <div className="flex flex-wrap gap-1.5">
          {product.nutrients.map((n) =>
          <div
            key={n.label}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}>
            
              <span className="text-[8px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>{n.label}</span>
              <span className="text-[9px] font-bold" style={{ color: product.textAccent }}>{n.value}</span>
            </div>
          )}
        </div>
      </div>

      {/* Arrow */}
      <div
        className="relative z-10 shrink-0 flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300"
        style={{
          background: `${product.textAccent}18`,
          border: `1px solid ${product.textAccent}30`,
          transform: hovered ? 'translate(4px, 0)' : 'translate(0,0)'
        }}>
        
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={product.textAccent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </div>
    </Link>);

}

/* ─── COMPACT CARD (spans 1 col × 1 row) ─── */
function CompactCard({ product }: {product: Product;}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      href={product.href}
      className="group relative flex flex-col justify-between overflow-hidden rounded-3xl min-h-[200px] p-6"
      style={{ background: '#FAF7F2', border: '1px solid #EDE8E0' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] transition-transform duration-300 origin-left"
        style={{ background: product.textAccent, transform: hovered ? 'scaleX(1)' : 'scaleX(0)' }} />
      
      <div className="relative z-10 flex items-start justify-between">
        {product.badge ?
        <span className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${badgeClasses[product.badgeStyle || 'neutral']}`}>
            {product.badge}
          </span> :
        <span />}
        <div
          className="flex items-center justify-center w-8 h-8 rounded-full bg-stone-100 border border-stone-200 transition-all duration-300"
          style={{ transform: hovered ? 'translate(3px, -3px)' : 'translate(0,0)' }}>

          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#78716c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17L17 7M17 7H7M17 7v10" />
          </svg>
        </div>
      </div>
      {/* Decorative product image, bleeding off the bottom-right corner */}
      <div
        className="absolute -right-5 -bottom-5 pointer-events-none transition-transform duration-500"
        style={{
          width: '132px',
          height: '132px',
          transform: hovered ? 'scale(1.08) translateY(-4px)' : 'scale(1)',
          filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.12))'
        }}>

        <AppImage src={product.image} alt={product.alt} width={132} height={132} className="object-contain w-full h-full" />
      </div>
      <div className="relative z-10">
        <h3 className="font-display font-[200] text-stone-900 leading-tight mb-1 max-w-[115px]" style={{ fontSize: '1.05rem', letterSpacing: '0.02em' }}>
          {product.name}
        </h3>
        <p className="text-xs text-stone-500 leading-relaxed mb-3 max-w-[115px]">{product.tagline}</p>
        <div className="flex flex-wrap gap-1 max-w-[115px]">
          {product.nutrients.slice(0, 2).map((n) =>
          <div key={n.label} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-stone-100">
              <span className="text-[8px] uppercase tracking-wider text-stone-400">{n.label}</span>
              <span className="text-[9px] font-bold text-stone-700">{n.value}</span>
            </div>
          )}
        </div>
      </div>
    </Link>);

}

export default function ProductsSection() {
  // Bento layout:
  // Row 1-2: [HeroCard(2×2)] [TallCard(1×2)] [TallCard(1×2)]
  // Row 3:   [WideCard(2×1)] [CompactCard(1×1)] [CompactCard(1×1)]  — but we only have 6 products
  // Actual: hero=products[0], tall1=products[1], tall2=products[2], wide=products[3], compact1=products[4], compact2=products[5]
  const [hero, tall1, tall2, wide, compact1, compact2] = products;

  return (
    <section id="products" className="pt-10 md:pt-14 pb-20 md:pb-28" style={{ background: '#FDFAF6' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Section header */}
        <div className="mb-12 md:mb-16 reveal">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-10" style={{ background: '#D32328' }} />
                <span className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#D32328' }}>
                  Our Range
                </span>
              </div>
              <h2
                className="font-display leading-tight text-stone-900"
                style={{ fontSize: 'clamp(2.4rem, 5vw, 4rem)', fontWeight: 200, letterSpacing: '0.02em' }}>
                
                Products built
                <br />
                <em className="not-italic" style={{ color: '#8A6A10' }}>for your table.</em>
              </h2>
            </div>
            <div className="md:max-w-[280px] md:pb-1">
              <p className="text-sm leading-relaxed text-stone-500">
                Every product is made with clean ingredients, honest labels, and zero compromise on nutrition.
              </p>
            </div>
          </div>
        </div>

        {/* ─── BENTO GRID (desktop: 4 cols) ─── */}
        <div className="hidden md:grid grid-cols-4 gap-4 auto-rows-[210px]">
          {/* Hero: col 1-2, row 1-2 */}
          <HeroCard product={hero} />
          {/* Tall 1: col 3, row 1-2 */}
          <TallCard product={tall1} />
          {/* Tall 2: col 4, row 1-2 */}
          <TallCard product={tall2} />
          {/* Wide: col 1-2, row 3 */}
          <WideCard product={wide} />
          {/* Compact 1: col 3, row 3 */}
          <CompactCard product={compact1} />
          {/* Compact 2: col 4, row 3 */}
          <CompactCard product={compact2} />
        </div>

        {/* ─── MOBILE: simple 1-col stack ─── */}
        <div className="md:hidden flex flex-col gap-4">
          {products.map((product) =>
          <Link
            key={product.id}
            href={product.href}
            className="group relative flex items-center gap-5 overflow-hidden rounded-2xl p-5"
            style={{ background: '#FAF7F2', border: '1px solid #EDE8E0' }}>
            
              <div className="shrink-0 w-20 h-20 rounded-xl overflow-hidden flex items-center justify-center" style={{ background: product.accent }}>
                <AppImage src={product.image} alt={product.alt} width={72} height={72} className="object-contain w-full h-full p-1" />
              </div>
              <div className="flex-1 min-w-0">
                {product.badge &&
              <span className={`inline-block text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mb-1.5 ${badgeClasses[product.badgeStyle || 'neutral']}`}>
                    {product.badge}
                  </span>
              }
                <h3 className="font-display font-[200] text-stone-900 text-base leading-tight mb-0.5">{product.name}</h3>
                <p className="text-xs text-stone-500 leading-relaxed line-clamp-2">{product.tagline}</p>
              </div>
              <svg className="shrink-0" style={{ color: 'var(--accent-ink)' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Link>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 flex justify-center reveal">
          <Link
            href="/products"
            className="inline-flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest transition-colors duration-200 group"
            style={{ color: '#8A6A10' }}>

            <span>View all products</span>
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full transition-transform duration-200 group-hover:translate-x-1" style={{ background: '#F9DC2A', color: '#1A1614' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </span>
          </Link>
        </div>
      </div>
    </section>);

}